import router from '@/router'
import store from '@/store'
import * as utilString from '@/utils/String'
import * as utilToken from '@/utils/Token'

let isAppUpdating = false
let isMaintenance = false

export const httpPromises = []

const isNoCacheReloadUpdated = () => {
  // ローカルストレージに'appUpdate'があればノーキャッシュリロードアップロードによるリロードが済んでいる
  return !!localStorage.getItem('appUpdate')
}

const noCacheReloadUpdate = afterVersion => {
  // 後続APIによってリロードが繰り返されることを防ぐ
  isAppUpdating = true

  // アップデート完了メッセージのループ防止のためのフラグ + そのアップデート情報として用いる
  // 表示後にremoveItemする
  localStorage.setItem('appUpdate', JSON.stringify({
    beforeVersion: process.env.APP_VERSION,
    afterVersion,
  }))

  // キャッシュを削除してリロードする
  location.reload(true)
}

export const http = ({ type, url, output = 'json', params = {}, options = {} }) => {
  let fetchOptions = {
    headers: {}
  }
  switch (type) {
    case 'GET':
      fetchOptions.method = 'GET'
      break
    case 'POST':
      fetchOptions.method = 'POST'
      fetchOptions.body = params
      break
  }

  url = process.env.VUE_APP_API_HOST + url

  const token = utilToken.get()
  if (token) {
    fetchOptions.headers['X-Authorization-Token'] = token
  }

  fetchOptions.headers['X-App-Version'] = process.env.APP_VERSION

  fetchOptions = {
    ...fetchOptions,
    ...options,
    headers: {
      ...fetchOptions.headers,
      ...options.headers,
    }
  }

  // ヘッダーのステータスを処理する
  const handleHeader = async (resp) => {
    if (isMaintenance) {
      // メンテナンス検知後の処理
      // 後続APIを叩かないように
      throw new Error('maintenance')
    }

    if (process.env.VUE_APP_ENV === 'localdev' &&
        resp.redirected &&
        /maintenance\.html$/.test(resp.url)
    ) {
      // localdev用メンテナンス
      // ドメイン, サイトIDは クエリパラメータで渡す
      isMaintenance = true
      const respUrlObj = new URL(resp.url)
      const host = (new URL(process.env.VUE_APP_VUE_HOST)).host
      // メンテナンスページから戻ってくる用のパラメータ
      respUrlObj.searchParams.append('path', location.pathname)
      respUrlObj.searchParams.append('host', host)
      location.href = respUrlObj.href
      throw new Error('maintenance')
    }

    if (resp.status === 503) {
      // メンテナンス
      isMaintenance = true
      location.reload(true)
      throw new Error('maintenance')
    }

    if (resp.ok) {
      if (output === 'blob') {
        let fileName = ''
        let match
        const disposition = resp.headers.get('Content-Disposition')
        const length = resp.headers.get('Content-Length')
        if (disposition) {
          if ((match = disposition.match(/filename="(.*)"/u))) {
            // 添付ファイルのファイル名がある場合は切り出す
            fileName = match[1]
          }
          // API返り値はblobとして扱う
          const blob = await resp.blob()
          if (length && blob.size !== Number(length)) {
            throw new Error('broken response')
          }
          return ({ blob: blob, fileName: fileName, fileSize: blob.size, respJson: null })
        }
        // 添付ファイルが存在しない場合はエラー等json返り値とみなす
        const json = await resp.json()
        return ({ blob: null, fileName: null, fileSize: null, respJson: json })
      }
      return resp.json()
    }
    throw new Error('invalid response')
  }

  // APIが返したステータスを処理する
  const handleAPI = json => {
    if (isAppUpdating) {
      return // 更新中は処理しない
    }
    if (output === 'blob') {
      if (json.blob) {
        // blob受取の場合はjsonデータが無いので処理しない
        return json
      }
      // エラー等がある場合はjsonで返るのでそれを処理する
      json = json.respJson
    }

    if (!json) throw new Error('json not found')

    if (json.appVersion == null) throw new Error('json.appVersion is not defined')

    const { isLocalStorageUpdate, isNoCacheReloadUpdate } = utilString.getVersionInfo(process.env.APP_VERSION, json.appVersion)
    // ローカルストレージを全クリア
    if (isLocalStorageUpdate) {
      localStorage.clear()
    }
    if (isNoCacheReloadUpdate) {
      // ノーキャッシュリロードアップデートの場合はリロード処理等を実行する
      !isNoCacheReloadUpdated() && noCacheReloadUpdate(json.appVersion)
      return json
    }

    // フォームバリデーションが返っていればそのまま通す
    switch (json.status) {
      case 200:
        if (json.messages && json.messages.success && json.messages.success.length > 0) {
          store.commit('mergeMessagePopup', {
            isShow: true,
            isSuccess: true,
            title: '',
            messages: json.messages.success
          })
        }
        return json
      case 401:
        // Unauthorized(401)
        if (json.messages && json.messages.error && json.messages.error.length > 0) {
          if (utilToken.get()) { // 何度もメッセージ出さないように
            store.commit('mergeMessagePopup', {
              isShow: true,
              isSuccess: false,
              title: '',
              messages: json.messages.error
            })
          }
        }
        // トークンを削除してログイン画面へ
        utilToken.remove()
        router.push({ name: 'login', query: { redirect: router.app._route.path } })
        return
    }

    // validation のうち、フォームバリデーションのものがあれば、そのまま通して、コンポーネントでハンドリングする
    if (json.formValidationKeys && json.validations) {
      for (const key of Object.keys(json.validations)) {
        if (json.formValidationKeys.includes(key)) return json
      }
    }

    let popupErrorMessages = []
    // messages.error とフォームバリデーション以外の validations はポップアップエラーメッセージに追加
    if (json.messages && json.messages.error && json.messages.error.length > 0) {
      popupErrorMessages = popupErrorMessages.concat(json.messages.error)
    }
    if (json.validations) {
      Object.keys(json.validations).forEach(key => {
        popupErrorMessages.push(json.validations[key])
      })
    }
    if (popupErrorMessages.length > 0) {
      store.commit('mergeMessagePopup', {
        isShow: true,
        isSuccess: false,
        title: '',
        messages: popupErrorMessages
      })
    } else {
      store.commit('mergeMessagePopup', {
        isShow: true,
        isSuccess: false,
        title: '',
        messages: ['不明なエラーです']
      })
    }
    return json
  }

  // 投げたエラーをキャッチする
  const handleError = error => {
    // プレビューページ等ではwindow.close()等ユーザー操作などによりリクエストがキャンセルされることがある
    if (error.message === 'Failed to fetch' && router.currentRoute.name === 'contentPreview') {
      throw new Error('操作によりリクエストがキャンセルされました')
    }
    // メンテナンス時にメッセージが表示されるのを防ぐ
    if (error.message === 'maintenance') return
    // ネットワークエラーとして表示する
    store.commit('mergeMessagePopup', {
      isShow: true,
      isSuccess: false,
      title: 'ネットワークエラー',
      messages: ['時間をおいてから再度アクセスしてください'],
    })
    throw new Error(error)
  }

  let pushed = false
  let popped = false
  const promise = fetch(url, fetchOptions)
    .then(resp => handleHeader(resp))
    .then(json => handleAPI(json))
    .catch(error => handleError(error))
    .finally(() => {
      pushed && httpPromises.pop()
      popped = true
    })
  if (!popped) {
    httpPromises.push(promise)
    pushed = true
  }
  return promise
}
